/**
 * Settings → AI's Models card (slice 1c spec §6.1, slice 2f spec §9): one
 * row per role. Thinks with is the default model; If that fails, the
 * fallbacks in order; Looks at photos with, the image model and its
 * fallbacks, or the default itself when it takes images. Every edit goes to
 * the routing draft (slice 1j spec §3); the bar under the cards saves.
 */
import { Button } from "@crew/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@crew/ui/components/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { FieldError } from "@crew/ui/components/field";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { Ellipsis, Image, ListOrdered, Sparkles, type LucideIcon } from "lucide-react";
import { useId, type ReactNode } from "react";
import type { Me } from "../../app/me";
import { useModels } from "../setup/api";
import { ModelPicker } from "../setup/model-picker";
import { modelLabel, type Catalog } from "../setup/onboarding";
import { Arrows, Show } from "./ai-parts";
import {
  addFallback,
  fallbackChoices,
  fallbackRow,
  makeDefault,
  MAX_FALLBACKS,
  modelOptions,
  moveFallback,
  removeFallback,
  routingNote,
  routingSaving,
  selectItems,
  setDefault,
  viaText,
  type ModelOptionGroup,
  type Routing,
} from "./ai-view";
import {
  addImageFallback,
  imageFallbackChoices,
  imageOptions,
  MAX_IMAGE_FALLBACKS,
  removeImageFallback,
  seesImages,
  setImageModel,
  type ImageRouting,
} from "./image-view";
import type { RoutingDraft } from "./use-routing-draft";

type Ai = NonNullable<Me["computer"]>["ai"];

type Edit = (routing: Routing) => Routing;

/** A picker of the routing's models, grouped by provider, then generation. */
function ModelSelect({
  groups,
  catalog,
  value,
  onChange,
  disabled,
  label,
  hint,
  placeholder,
  via,
  className,
}: {
  groups: ModelOptionGroup[];
  catalog: Catalog | undefined;
  value: string | null;
  onChange: (model: string) => void;
  disabled: boolean;
  label: string;
  /** The role's line, under the sheet's title on a phone. */
  hint?: string;
  placeholder?: string;
  via?: ReactNode;
  className?: string;
}) {
  // The current model as the list names it, even when it's no longer offered.
  const current = selectItems(groups, catalog, value).find((i) => i.value === value)?.label;
  return (
    <ModelPicker
      label={label}
      hint={hint}
      placeholder={placeholder}
      via={via}
      groups={groups}
      value={value}
      currentLabel={current}
      disabled={disabled}
      onChange={onChange}
      tagImages
      className={className}
    />
  );
}

/**
 * One role: its icon, name and line; its picker beside them on a desktop,
 * under them on a phone; and anything more (a list) under both.
 */
function Role({
  icon: Icon,
  title,
  hint,
  control,
  children,
  ...data
}: {
  icon: LucideIcon;
  title: string;
  hint: string;
  control?: ReactNode;
  children?: ReactNode;
  [data: `data-${string}`]: string | boolean | undefined;
}) {
  const id = useId();
  return (
    <div
      role="group"
      aria-labelledby={id}
      className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-3 border-t border-border py-5 first:border-t-0 first:pt-0 last:pb-0 sm:grid-cols-[auto_1fr_22rem] sm:gap-x-3.5"
      {...data}
    >
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground sm:size-8">
        <Icon className="size-4" />
      </span>
      <div className={cn("flex min-w-0 flex-col", !control && "sm:col-span-2")}>
        <p id={id} className="text-body font-medium text-foreground">
          {title}
        </p>
        <p className="text-meta text-muted-foreground">{hint}</p>
      </div>
      <Show when={control}>
        <div className="col-span-2 flex flex-col gap-1.5 sm:col-span-1">{control}</div>
      </Show>
      <Show when={children}>
        <div className="col-span-2 sm:col-span-2 sm:col-start-2">{children}</div>
      </Show>
    </div>
  );
}

/** A fallback's Make default and Remove, behind ⋯. */
function FallbackMenu({
  name,
  disabled,
  onDefault,
  onRemove,
}: {
  name: string;
  disabled: boolean;
  onDefault?: () => void;
  onRemove: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`More for ${name}`}
            className="text-muted-foreground"
          />
        }
      >
        <Ellipsis />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44 p-1.5">
        <Show when={onDefault}>
          <DropdownMenuItem onClick={onDefault} className="font-medium">
            Make default
          </DropdownMenuItem>
        </Show>
        <DropdownMenuItem onClick={onRemove} variant="destructive" className="font-medium">
          Remove
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A numbered model in a list: its name, who serves it, and what can be done with it. */
function ListedModel({
  index,
  name,
  via,
  children,
  ...data
}: {
  index: number;
  name: string;
  via: string | null;
  children: ReactNode;
  [data: `data-${string}`]: string;
}) {
  return (
    <li className="flex items-center gap-3 py-2.5 pr-2 pl-3.5" {...data}>
      <span className="w-3 shrink-0 font-mono text-2xs text-muted-foreground tabular-nums">
        {index + 1}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body text-foreground">{name}</span>
        <span className="truncate text-meta text-muted-foreground">{via}</span>
      </span>
      <span className="flex shrink-0 items-center gap-0.5">{children}</span>
    </li>
  );
}

const listBox =
  "flex flex-col divide-y divide-border rounded-lg bg-muted/50 ring-1 ring-foreground/8";

function Fallbacks({
  ai,
  routing,
  options,
  catalog,
  disabled,
  onEdit,
}: {
  ai: Ai;
  routing: Routing;
  options: ModelOptionGroup[];
  catalog: Catalog | undefined;
  disabled: boolean;
  onEdit: (edit: Edit) => void;
}) {
  const choices = fallbackChoices(options, routing);
  const full = routing.fallbacks.length >= MAX_FALLBACKS;
  return (
    <div className="flex flex-col gap-3">
      <Show when={routing.fallbacks.length > 0}>
        <ol aria-label="Fallback order" className={listBox}>
          {routing.fallbacks.map((model, i) => {
            const row = fallbackRow(routing, i, ai, catalog);
            return (
              <ListedModel
                key={model}
                index={i}
                name={row.name}
                via={row.via}
                data-fallback={model}
              >
                <Arrows
                  name={{ up: `Move ${row.name} up`, down: `Move ${row.name} down` }}
                  up={row.up}
                  down={row.down}
                  disabled={disabled}
                  onMove={(direction) => onEdit((r) => moveFallback(r, i, direction))}
                />
                <FallbackMenu
                  name={row.name}
                  disabled={disabled}
                  onDefault={() => onEdit((r) => makeDefault(r, i))}
                  onRemove={() => onEdit((r) => removeFallback(r, i))}
                />
              </ListedModel>
            );
          })}
        </ol>
      </Show>
      {full ? (
        <p className="text-meta text-muted-foreground">Up to five fallback models.</p>
      ) : (
        <Show when={choices.length > 0}>
          <ModelSelect
            key={routing.fallbacks.join()}
            label="Add a fallback"
            hint="Tried in this order when it fails or hits a limit."
            placeholder="Add a fallback…"
            groups={choices}
            catalog={catalog}
            value={null}
            disabled={disabled}
            onChange={(model) => onEdit((r) => addFallback(r, model))}
            className="border border-dashed border-input shadow-none"
          />
        </Show>
      )}
    </div>
  );
}

function ImageFallbacks({
  ai,
  image,
  options,
  catalog,
  disabled,
  onEdit,
}: {
  ai: Ai;
  image: NonNullable<ImageRouting>;
  options: ModelOptionGroup[];
  catalog: Catalog | undefined;
  disabled: boolean;
  onEdit: (next: ImageRouting) => void;
}) {
  const choices = imageFallbackChoices(options, image);
  return (
    <div className="flex flex-col gap-3">
      <Show when={image.fallbacks.length > 0}>
        <ol aria-label="Image fallbacks" className={listBox}>
          {image.fallbacks.map((model, i) => {
            const name = modelLabel(catalog, model) ?? model;
            return (
              <ListedModel
                key={model}
                index={i}
                name={name}
                via={viaText(model, ai)}
                data-image-fallback={model}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  aria-label={`Remove ${name}`}
                  onClick={() => onEdit(removeImageFallback(image, i))}
                >
                  Remove
                </Button>
              </ListedModel>
            );
          })}
        </ol>
      </Show>
      <Show when={image.fallbacks.length < MAX_IMAGE_FALLBACKS && choices.length > 0}>
        <ModelSelect
          key={image.fallbacks.join()}
          label="Add an image fallback"
          placeholder="Add an image fallback…"
          groups={choices}
          catalog={catalog}
          value={null}
          disabled={disabled}
          onChange={(model) => onEdit(addImageFallback(image, model))}
          className="border border-dashed border-input shadow-none"
        />
      </Show>
    </div>
  );
}

/** Under the photos picker: who serves it and Turn off, the default seeing them itself, or why none. */
function PhotosNote({
  ai,
  image,
  itself,
  none,
  catalog,
  disabled,
  onOff,
}: {
  ai: Ai;
  image: ImageRouting;
  itself: boolean;
  none: boolean;
  catalog: Catalog | undefined;
  disabled: boolean;
  onOff: () => void;
}) {
  if (image) {
    return (
      <div className="flex items-center gap-3">
        <p className="flex-1 text-meta text-muted-foreground">{viaText(image.model, ai)}</p>
        {/* Text only, so its edge is the picker's. */}
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={onOff}
          className="h-auto px-0 text-meta hover:bg-transparent hover:underline"
        >
          Turn off
        </Button>
      </div>
    );
  }
  if (itself) {
    return (
      <p className="text-meta text-muted-foreground" data-image-itself>
        {modelLabel(catalog, ai.model) ?? "The default model"} sees images itself.
      </p>
    );
  }
  return none ? (
    <p className="text-meta text-muted-foreground" data-image-none>
      None of your connected providers' models can look at images. Connect one that can, such as
      ChatGPT or Claude.
    </p>
  ) : null;
}

/** Looks at photos with (slice 2f §9): the image model, or the default when it takes images. */
function PhotosRole({
  ai,
  draft,
  catalog,
  disabled,
}: {
  ai: Ai;
  draft: RoutingDraft;
  catalog: Catalog | undefined;
  disabled: boolean;
}) {
  const options = imageOptions(catalog, ai);
  const image = draft.routing.image;
  const itself = seesImages(catalog, draft.routing.model);
  const apply = (next: ImageRouting) => draft.edit((r) => ({ ...r, image: next }));
  const none = options.length === 0 && !image;
  return (
    <Role
      icon={Image}
      title="Looks at photos with"
      hint="Like a receipt someone texts in."
      data-image-model
      control={
        <>
          <Show when={!none}>
            <ModelSelect
              label="Looks at photos with"
              hint="Like a receipt someone texts in."
              placeholder={itself ? "Same as Thinks with" : "None: Crew can't look at photos"}
              groups={options}
              catalog={catalog}
              value={image?.model ?? null}
              disabled={disabled}
              onChange={(model) => apply(setImageModel(image, model))}
            />
          </Show>
          <PhotosNote
            ai={ai}
            image={image}
            itself={itself}
            none={none}
            catalog={catalog}
            disabled={disabled}
            onOff={() => apply(null)}
          />
          {image ? (
            <ImageFallbacks
              ai={ai}
              image={image}
              options={options}
              catalog={catalog}
              disabled={disabled}
              onEdit={apply}
            />
          ) : null}
        </>
      }
    />
  );
}

function Saving({ when }: { when: boolean }) {
  return (
    <Show when={when}>
      <CardAction>
        <span className="flex items-center gap-1.5 text-meta text-muted-foreground">
          <Spinner className="size-3.5" />
          Saving…
        </span>
      </CardAction>
    </Show>
  );
}

/** The Models card: Thinks with, If that fails, and Looks at photos with. */
export function ModelsCard({ ai, busy, draft }: { ai: Ai; busy: boolean; draft: RoutingDraft }) {
  const models = useModels();
  const catalog = models.data;
  const routing = draft.routing;
  const options = modelOptions(catalog, ai);
  const disabled = busy || draft.saving;
  const onEdit = (edit: Edit) => draft.edit((r) => ({ ...r, ...edit(r) }));
  const note = routingNote(null, null, ai);
  return (
    <Card role="region" aria-label="Models" data-routing>
      <CardHeader>
        <CardTitle>Models</CardTitle>
        <CardDescription>
          Who your Chief of Staff thinks with, who steps in, and who looks at photos. A change
          applies from the next message.
        </CardDescription>
        {/* A run this tab didn't start (the bar shows this tab's own save). */}
        <Saving when={routingSaving(false, ai) && !draft.saving} />
      </CardHeader>
      <CardContent className="flex flex-col">
        <Role
          icon={Sparkles}
          title="Thinks with"
          hint="Every ask starts here."
          control={
            <>
              <ModelSelect
                label="Thinks with"
                hint="Every ask starts here."
                via={
                  routing.model ? (
                    <span data-default-via>{viaText(routing.model, ai)}</span>
                  ) : undefined
                }
                groups={options}
                catalog={catalog}
                value={routing.model}
                disabled={disabled}
                onChange={(model) => onEdit((r) => setDefault(r, model))}
              />
            </>
          }
        />
        <Role
          icon={ListOrdered}
          title="If that fails"
          hint={
            routing.fallbacks.length > 0
              ? "Tried in this order when it fails or hits a limit."
              : "No fallbacks. When it fails, the ask fails."
          }
        >
          <Fallbacks
            ai={ai}
            routing={routing}
            options={options}
            catalog={catalog}
            disabled={disabled}
            onEdit={onEdit}
          />
        </Role>
        <PhotosRole ai={ai} draft={draft} catalog={catalog} disabled={disabled} />
        <Show when={note}>
          <FieldError className="pt-4">{note}</FieldError>
        </Show>
      </CardContent>
    </Card>
  );
}
